import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import type { AxiosError } from 'axios';
import { Button, Select, Form, Label } from '@shared/components';
import { readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { useSecurityGroupsQuery } from '@/features/security-admin/hooks/useSecurityGroupsQuery';
import { useAttachDealerToGroupMutation } from '@/features/security-admin/hooks/useAttachDealerToGroupMutation';

const schema = z.object({
  securityGroupId: z.string().uuid({ message: 'Select a security group' }),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  dealerId: string;
  dealerName: string;
  attachedGroupIds: string[];
  onClose: () => void;
}

/**
 * Inline panel (not a modal) for attaching this dealer to a security group.
 * Rendered in the attach panel below the security-groups table, following the
 * shared "Add New / Attach" layout (see DealerContacts).
 */
export function AttachDealerToGroupPanel({ dealerId, dealerName, attachedGroupIds, onClose }: Props) {
  const groupsQuery = useSecurityGroupsQuery({ per_page: 50 }); // security-service caps per_page at 50
  const allGroups = groupsQuery.data?.data ?? [];
  const availableGroups = allGroups.filter((g) => !attachedGroupIds.includes(g.id));

  const mutation = useAttachDealerToGroupMutation(dealerId);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { control, handleSubmit, watch, formState: { isValid } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const selectedGroupId = watch('securityGroupId');
  const selectedGroupName = availableGroups.find((g) => g.id === selectedGroupId)?.name ?? '';

  const isAllAttached = !groupsQuery.isLoading && !groupsQuery.isError && availableGroups.length === 0;

  const onSubmit = ({ securityGroupId }: FormValues) => {
    setSubmitError(null);
    mutation.mutate(securityGroupId, {
      onSuccess: () => {
        toast.success(`${dealerName} attached to ${selectedGroupName}.`);
        onClose();
      },
      onError: (err: unknown) => {
        const { message } = readApiError((err as AxiosError).response?.data);
        const msg =
          message === DEFAULT_ERROR_MESSAGE
            ? 'Failed to attach to security group. Please try again.'
            : message;
        setSubmitError(msg);
        toast.error(msg);
      },
    });
  };

  return (
    <Form onSubmit={(e) => { void handleSubmit(onSubmit)(e); }} className="space-y-4 max-w-lg">
      <Label htmlFor="security-group-select">Attach {dealerName} to a Security Group</Label>

      {groupsQuery.isLoading ? (
        <p className="text-sm text-mp-muted">Loading security groups...</p>
      ) : groupsQuery.isError ? (
        <p className="text-mp-error text-sm">Failed to load security groups. Please try again.</p>
      ) : (
        <Controller
          name="securityGroupId"
          control={control}
          render={({ field }) => (
            <Select
              id="security-group-select"
              options={availableGroups.map((g) => ({ value: g.id, label: g.name }))}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Select a security group"
              disabled={mutation.isPending || isAllAttached}
            />
          )}
        />
      )}

      {isAllAttached && (
        <p className="text-sm text-mp-muted">This dealer is already in every security group.</p>
      )}

      {submitError && (
        <p className="text-mp-error text-xs">{submitError}</p>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!isValid || mutation.isPending || isAllAttached || groupsQuery.isError}
        >
          {mutation.isPending ? 'Attaching...' : 'Attach'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}
